import { useServerMonitoring } from '@/hooks/useServerMonitoring'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { 
  Activity, 
  Cpu, 
  HardDrive, 
  Clock, 
  Users, 
  Zap,
  AlertTriangle,
  CheckCircle,
  XCircle,
  RefreshCw,
  Wifi,
  WifiOff,
  Monitor,
  Database,
  Eye,
  Server as ServerIcon
} from 'lucide-react'

interface ServerMonitorProps {
  serverId: string
  className?: string
}

export function ServerMonitor({ serverId, className }: ServerMonitorProps) {
  const { status, isLoading, error, isConnected, systemMonitoring, refresh } = useServerMonitoring({
    serverId,
    enabled: true,
    interval: 10000 // 10 second updates
  })

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'running': return 'text-green-600 bg-green-100'
      case 'stopped': return 'text-red-600 bg-red-100'
      case 'starting': return 'text-yellow-600 bg-yellow-100'
      case 'error': return 'text-red-600 bg-red-100'
      default: return 'text-gray-600 bg-gray-100'
    }
  }

  const getStatusIcon = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'running': return <CheckCircle className="h-4 w-4" />
      case 'stopped': return <XCircle className="h-4 w-4" />
      case 'starting': return <RefreshCw className="h-4 w-4 animate-spin" />
      case 'error': return <AlertTriangle className="h-4 w-4" />
      default: return <Activity className="h-4 w-4" />
    }
  }

  const formatUptime = (uptime: number) => {
    const seconds = Math.floor(uptime / 1000)
    const minutes = Math.floor(seconds / 60)
    const hours = Math.floor(minutes / 60)
    const days = Math.floor(hours / 24)

    if (days > 0) return `${days}d ${hours % 24}h`
    if (hours > 0) return `${hours}h ${minutes % 60}m`
    if (minutes > 0) return `${minutes}m ${seconds % 60}s`
    return `${seconds}s`
  }

  const formatResponseTime = (ms: number) => {
    if (ms < 1000) return `${Math.round(ms)}ms`
    return `${(ms / 1000).toFixed(2)}s`
  }

  const formatMemory = (mb: number) => {
    if (mb >= 1024) return `${(mb / 1024).toFixed(1)} GB`
    return `${Math.round(mb)} MB`
  }

  const getMonitoringSourceBadge = (source?: string) => {
    if (source === 'real_system_metrics') {
      return (
        <Badge variant="outline" className="text-green-600 border-green-200 bg-green-50">
          <Monitor className="h-3 w-3 mr-1" />
          Real Metrics
        </Badge>
      )
    }
    return (
      <Badge variant="outline" className="text-gray-600 border-gray-200 bg-gray-50">
        <Eye className="h-3 w-3 mr-1" />
        Simulated
      </Badge>
    )
  }

  if (isLoading && !status) {
    return (
      <Card className={className}>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Activity className="h-5 w-5 animate-pulse" />
            <span>Server Monitor</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <RefreshCw className="h-6 w-6 animate-spin text-blue-500" />
            <span className="ml-2 text-muted-foreground">Loading server status...</span>
          </div>
        </CardContent>
      </Card>
    )
  }

  if (error && !status) {
    return (
      <Card className={className}>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <AlertTriangle className="h-5 w-5 text-red-500" />
            <span>Server Monitor</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">
            <p className="text-red-600 mb-4">Failed to load server status</p>
            <p className="text-sm text-muted-foreground mb-4">{error.message}</p>
            <Button onClick={refresh} variant="outline">
              <RefreshCw className="mr-2 h-4 w-4" />
              Retry
            </Button>
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card className={className}>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center space-x-2">
            <Activity className="h-5 w-5" />
            <span>Server Monitor</span>
            {status?.metrics.monitoring_source && getMonitoringSourceBadge(status.metrics.monitoring_source)}
          </CardTitle>
          <div className="flex items-center space-x-2">
            <div className="flex items-center space-x-1">
              {isConnected ? (
                <Wifi className="h-4 w-4 text-green-500" />
              ) : (
                <WifiOff className="h-4 w-4 text-red-500" />
              )}
              <span className="text-xs text-muted-foreground">
                {isConnected ? 'Live' : 'Disconnected'}
              </span>
            </div>
            <Button variant="ghost" size="sm" onClick={refresh}>
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
        </div>
        <CardDescription>
          Real-time server monitoring and metrics
          {status?.metrics.monitoring_source === 'real_system_metrics' && (
            <span className="text-green-600"> • Live system data</span>
          )}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {status && (
          <div className="space-y-6">
            {/* Status Overview */}
            <div className="flex items-center space-x-4">
              <div className={`flex items-center space-x-2 px-3 py-1 rounded-full ${getStatusColor(status.status)}`}>
                {getStatusIcon(status.status)}
                <span className="text-sm font-medium capitalize">{status.status}</span>
              </div>
              <div className="text-sm text-muted-foreground">
                Port: {status.port}
              </div>
              <div className="text-sm text-muted-foreground">
                Uptime: {formatUptime(status.metrics.uptime)}
              </div>
              {status.metrics.process_id && (
                <div className="text-sm text-muted-foreground">
                  PID: {status.metrics.process_id}
                </div>
              )}
            </div>

            {/* Key Metrics Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center">
                <div className="flex items-center justify-center mb-2">
                  <Cpu className="h-5 w-5 text-blue-500" />
                </div>
                <div className="text-2xl font-bold">{Math.round(status.metrics.cpu_usage)}%</div>
                <div className="text-xs text-muted-foreground">
                  CPU Usage
                  {status.metrics.monitoring_source === 'real_system_metrics' && (
                    <span className="text-green-600"> (Live)</span>
                  )}
                </div>
              </div>

              <div className="text-center">
                <div className="flex items-center justify-center mb-2">
                  <HardDrive className="h-5 w-5 text-green-500" />
                </div>
                <div className="text-2xl font-bold">{Math.round(status.metrics.memory_usage)}%</div>
                <div className="text-xs text-muted-foreground">
                  Memory
                  {status.metrics.memory_mb && (
                    <div className="text-xs text-gray-500">
                      {formatMemory(status.metrics.memory_mb)}
                    </div>
                  )}
                  {status.metrics.monitoring_source === 'real_system_metrics' && (
                    <span className="text-green-600"> (Live)</span>
                  )}
                </div>
              </div>

              <div className="text-center">
                <div className="flex items-center justify-center mb-2">
                  <Clock className="h-5 w-5 text-orange-500" />
                </div>
                <div className="text-2xl font-bold">{formatResponseTime(status.metrics.response_time_avg)}</div>
                <div className="text-xs text-muted-foreground">Avg Response</div>
              </div>

              <div className="text-center">
                <div className="flex items-center justify-center mb-2">
                  <Users className="h-5 w-5 text-purple-500" />
                </div>
                <div className="text-2xl font-bold">{status.metrics.active_connections}</div>
                <div className="text-xs text-muted-foreground">Connections</div>
              </div>
            </div>

            {/* Real System Information (when available) */}
            {status.metrics.monitoring_source === 'real_system_metrics' && (
              <div className="border-t pt-4">
                <h4 className="text-sm font-medium mb-3 flex items-center">
                  <Monitor className="h-4 w-4 mr-2 text-green-600" />
                  Live System Information
                </h4>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {status.metrics.process_id && (
                    <div className="text-center p-3 bg-green-50 rounded-md">
                      <div className="text-sm font-medium text-green-800">Process ID</div>
                      <div className="text-lg font-bold text-green-600">{status.metrics.process_id}</div>
                    </div>
                  )}
                  {status.metrics.process_status && (
                    <div className="text-center p-3 bg-blue-50 rounded-md">
                      <div className="text-sm font-medium text-blue-800">Process Status</div>
                      <div className="text-lg font-bold text-blue-600 capitalize">{status.metrics.process_status}</div>
                    </div>
                  )}
                  {status.system_metrics && (
                    <div className="text-center p-3 bg-purple-50 rounded-md">
                      <div className="text-sm font-medium text-purple-800">System CPU</div>
                      <div className="text-lg font-bold text-purple-600">{Math.round(status.system_metrics.cpu_percent)}%</div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Performance Indicators */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-md">
                <div className="flex items-center space-x-2">
                  <Activity className="h-4 w-4 text-blue-500" />
                  <span className="text-sm">Requests</span>
                </div>
                <div className="text-sm font-semibold">{status.metrics.request_count?.toLocaleString() ?? '0'}</div>
              </div>

              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-md">
                <div className="flex items-center space-x-2">
                  <AlertTriangle className="h-4 w-4 text-red-500" />
                  <span className="text-sm">Error Rate</span>
                </div>
                <div className="text-sm font-semibold">{status.metrics.error_rate?.toFixed(2) ?? '0'}%</div>
              </div>

              {status.metrics.cache_hit_rate !== undefined && (
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-md">
                  <div className="flex items-center space-x-2">
                    <Database className="h-4 w-4 text-green-500" />
                    <span className="text-sm">Cache Hit Rate</span>
                  </div>
                  <div className="text-sm font-semibold">{status.metrics.cache_hit_rate?.toFixed(2)}%</div>
                </div>
              )}
            </div>

            {/* Health Status */}
            <div className="border-t pt-4">
              <h4 className="text-sm font-medium mb-3">Health Status</h4>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm">CPU Health</span>
                  <Badge variant={status.metrics.cpu_usage > 80 ? "destructive" : status.metrics.cpu_usage > 60 ? "secondary" : "outline"}>
                    {status.metrics.cpu_usage > 80 ? 'High' : status.metrics.cpu_usage > 60 ? 'Medium' : 'Good'}
                  </Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm">Memory Health</span>
                  <Badge variant={status.metrics.memory_usage > 80 ? "destructive" : status.metrics.memory_usage > 60 ? "secondary" : "outline"}>
                    {status.metrics.memory_usage > 80 ? 'High' : status.metrics.memory_usage > 60 ? 'Medium' : 'Good'}
                  </Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm">Response Time</span>
                  <Badge variant={status.metrics.response_time_avg > 1000 ? "destructive" : status.metrics.response_time_avg > 500 ? "secondary" : "outline"}>
                    {status.metrics.response_time_avg > 1000 ? 'Slow' : status.metrics.response_time_avg > 500 ? 'Medium' : 'Fast'}
                  </Badge>
                </div>
              </div>
            </div>

            {/* System Context (when real metrics available) */}
            {/* {status.system_metrics && (
              <div className="border-t pt-4">
                <h4 className="text-sm font-medium mb-3 flex items-center">
                  <ServerIcon className="h-4 w-4 mr-2" />
                  System Context
                </h4>
                <div className="text-sm text-muted-foreground space-y-1">
                  <div>System Memory: {Math.round(status.system_metrics.memory_percent)}% used ({formatMemory(status.system_metrics.memory_total / 1024 / 1024)} total)</div>
                  <div>System CPU: {Math.round(status.system_metrics.cpu_percent)}% usage</div>
                  {systemMonitoring?.host_info && (
                    <div>Host: {systemMonitoring.host_info.hostname} ({systemMonitoring.host_info.platform})</div>
                  )}
                </div>
              </div>
            )} */}
          </div>
        )}
      </CardContent>
    </Card>
  )
} 